import hash from 'object-hash';

import { addDebugBarData, mapRpcDebugBarData } from 'utility/Debug/DebugBar';
import { SearchDData } from 'utility/Extract/getSearchDData';
import { cacheRequest, cacheResponse } from 'utility/Request/cache';
import { callApi } from 'utility/Request/callApi';
import { pollSearchD } from 'utility/Request/Polling';
import { PollingEventType } from 'utility/Request/Polling/types';

import { ApiEndpoint } from 'types/Request/endpoint';

import { ActiveFilterResponse } from 'proto/Api/ActiveFilter';
import { ChatBotResponse } from 'proto/Api/ChatBot';
import { HotelListRequest, HotelListResponse } from 'proto/Api/Hotel/HotelList';

import { ActionMap } from '../../reducer/AccommodationList/types';

export const fetchAccommodationList = async (
    requestData: HotelListRequest,
    abortController?: AbortController,
    isDevicedActive?: boolean
) => {
    const apiUrl = isDevicedActive
        ? '/rpc/v2/desktop/hotel/list'
        : '/rpc/desktop/hotel/list';

    const response = await callApi(ApiEndpoint.HotelList, {
        requestData,
        abortController,
        url: apiUrl,
    });
    if (response.signal?.aborted) {
        throw new Error('aborted');
    }
    response.parsedBody?.debugBar?.contentOrder.length &&
        addDebugBarData(
            mapRpcDebugBarData(response.parsedBody.debugBar),
            ApiEndpoint.HotelList
        );

    return response.parsedBody ?? HotelListResponse.fromPartial({});
};

export const fetchActiveFilterList = async (
    searchUrl: string,
    abortController?: AbortController
) => {
    const response = await callApi(ApiEndpoint.ActiveFilterList, {
        requestData: {
            searchUrl,
            step: 3,
        },
        abortController,
    });
    addDebugBarData(
        mapRpcDebugBarData(response.parsedBody?.debugBar),
        ApiEndpoint.ActiveFilterList
    );
    return response.parsedBody ?? ActiveFilterResponse.fromPartial({});
};

export const startPolling = (
    pollingUrl: string,
    searchDData: SearchDData,
    actionMap: ActionMap,
    abortController?: AbortController
) => {
    pollSearchD({
        url: pollingUrl,
        requestData: {
            clientId: searchDData.clientId,
        },
        delay: searchDData.searchDPollInterval,
        events: {
            [PollingEventType.HotelList]: {
                timeout: searchDData.globalSearchTimeout,
                updateInterval: searchDData.updatedHotelListThreshold, // notifies an update every n finished supplier requests
                onTimeout: actionMap.onPollTimeout,
                onPollError: actionMap.onPollError,
                onPollUpdate: actionMap.onPollUpdate,
                onPollFinished: actionMap.onPollFinished,
            },
        },
        abortController,
    });
};

const fetchChatBotData = async (serviceCode: string) => {
    const response = await callApi(ApiEndpoint.ChatBot, {
        requestData: {
            serviceCode,
        },
    });
    return response.parsedBody ?? ChatBotResponse.fromPartial({});
};

const fetchChatBotDataCached = cacheRequest(
    cacheResponse(fetchChatBotData, hash),
    hash
);

export { fetchChatBotDataCached as fetchChatBotData };
